import {
  CHAT_MAIL_SETUP_ASK_ACTION,
  CHAT_MAIL_SETUP_ASK_TEXT,
} from "./albisVoice";
import { ITEM_TYPE_TASK, PARENT_MAIL_LABEL } from "./scheduleLabels";

/**
 * Scripted early-childhood mail forwarding setup in chat (no NLU).
 */

export const SCHOOL_SETUP_FLOW_TEXT = {
  intro: `Leite die letzte ${PARENT_MAIL_LABEL} aus deinem Postfach an diese Adresse weiter. Ich werte sie gleich aus. Flyer oder Screenshot: Foto hier im Chat, ich merke den Termin.`,
  chatIntro: `Leite die letzte ${PARENT_MAIL_LABEL} an diese Adresse weiter — oder lade den Flyer als Foto hier unten hoch. Ich erinnere dich an die Termine.`,
  addressLabel: "Deine Adresse:",
  copied: "Kopiert. Leite sie im Postfach weiter — ich melde mich, wenn sie da ist.",
  firstMailChip: "Ich habe sie weitergeleitet",
  waiting: `Ich warte auf die ${PARENT_MAIL_LABEL}.`,
  attachmentWait: `Die ${PARENT_MAIL_LABEL} ist da — der Anhang wird noch gelesen.`,
  stillWaiting: `Noch nichts angekommen. Leite die letzte ${PARENT_MAIL_LABEL} an die Adresse oben weiter. Ich schaue wieder, sobald du hier bist.`,
  mailFailed: `Die ${PARENT_MAIL_LABEL} ist angekommen, der Anhang konnte nicht gelesen werden. Unter Mehr bei KiTa & Kindergarten kannst du das PDF hochladen.`,
  gmailHint:
    "In Gmail (oft am Computer): Einstellungen → Weiterleitung → diese Adresse hinzufügen. Den Bestätigungscode zeige ich im Chat. Danach ein Filter nur für Mails von KiTa, Kindergarten oder Hort.",
  doneHint:
    "Sobald die letzte Eltern-Mail da ist, zeige ich die Fristen hier. Danach kannst du die Weiterleitung einmal einrichten.",
};

export const MAIL_SETUP_ASK_ID = "mail-setup-ask";

/** Spoken line. The address sits beside a copy icon, not in this sentence. */
export function buildSchoolSetupAddressMessage() {
  return SCHOOL_SETUP_FLOW_TEXT.chatIntro;
}

/**
 * First signed-in beat: forwarding alias, when nothing has arrived yet.
 * A flyer already uploaded (processing, ready, or confirmed) counts.
 * Cancelled and failed photos do not.
 *
 * @param {{
 *   hasExtractions?: boolean | null,
 *   hasFlyerUpload?: boolean | null,
 *   setupAlreadyDone?: boolean,
 *   profileReady?: boolean | null,
 * }} [args]
 */
export function shouldOfferMailSetupAsk(args = {}) {
  if (args.hasExtractions !== false) return false;
  if (args.hasFlyerUpload === true) return false;
  // A tapped "weitergeleitet" chip used to hide this step. With zero mails the
  // thread would open blank, so the address stays until something arrives.
  return true;
}

export function buildMailSetupAskMessage() {
  return {
    id: MAIL_SETUP_ASK_ID,
    role: "assistant",
    text: CHAT_MAIL_SETUP_ASK_TEXT,
    action: { type: "start_school_setup" },
    actionLabel: CHAT_MAIL_SETUP_ASK_ACTION,
  };
}

/**
 * @param {{ eventCount?: number, hasSummary?: boolean }} [args]
 */
export function buildFirstExtractionCelebrationMessage(args = {}) {
  const eventCount =
    typeof args === "number" ? args : Number(args.eventCount) || 0;
  const hasSummary = typeof args === "number" ? false : !!args.hasSummary;

  if (eventCount > 0) {
    return `${PARENT_MAIL_LABEL} erkannt. Wovor soll ich erinnern?`;
  }
  if (hasSummary) {
    return `${PARENT_MAIL_LABEL} erkannt — Kurzfassung liegt bereit. Sobald Fristen drin sind, kannst du Erinnerungen auswählen.`;
  }
  return `${PARENT_MAIL_LABEL} erkannt. Sobald Fristen drin sind, wählst du hier, wovor ich erinnere.`;
}

export function buildKitaRemindPickerIntro(count) {
  if (count === 1) {
    return "1 Frist erkannt — Erinnerung an?";
  }
  return `${count} Fristen erkannt — wovor soll ich erinnern?`;
}

export function buildKitaRemindPickerDoneMessage(count) {
  if (count === 0) {
    return "Alles klar — keine Erinnerungen aktiv.";
  }
  if (count === 1) {
    return "Erinnere dich vor 1 Frist.";
  }
  return `Erinnere dich vor ${count} Fristen.`;
}

export function buildKitaReminderCardMessage({ title, date, remindEnabled, reminderKind }) {
  const when = date || "bald";
  const titleText = title || ITEM_TYPE_TASK;
  const timing =
    reminderKind === "t3"
      ? "Noch 3 Tage —"
      : reminderKind === "t1"
        ? "Morgen —"
        : "";
  const remindHint = remindEnabled
    ? "Erinnerung ist an."
    : "Erinnerung ist aus — unten einschalten, wenn du möchtest.";
  return `${timing} ${titleText} (bis ${when}). ${remindHint}`;
}

/**
 * One-time filter setup after the first real result. Account-wide forwarding
 * is intentionally not the instruction — only mail from the Kita sender.
 *
 * @param {{ sender?: string|null, address?: string|null }} [args]
 */
export function buildAutoForwardPrompt({ sender, address } = {}) {
  const target = address || "deine Albis-Adresse";
  const fromFilter = sender ? `Von ${sender}` : "Von der KiTa";
  const who = sender || "die KiTa";
  const lead = sender
    ? `Damit die nächste ${PARENT_MAIL_LABEL} von allein ankommt: leite alles von ${sender} an ${target} weiter. Einmal einrichten.`
    : `Damit die nächste ${PARENT_MAIL_LABEL} von allein ankommt: leite Mails der KiTa an ${target} weiter. Einmal einrichten.`;
  return [
    lead,
    "",
    `Gmail (am Computer): Einstellungen → Weiterleitung → ${target} hinzufügen. Den Code zeige ich hier im Chat. Danach ein Filter: ${fromFilter} → Weiterleiten.`,
    `Outlook: Einstellungen → E-Mail → Regeln. ${fromFilter} an ${target}. Den Code zeige ich hier.`,
    `iCloud: Mail → Regeln. Wenn der Absender ${who} ist, weiterleiten an ${target}.`,
  ].join("\n");
}

/**
 * @param {{ provider?: string|null, code?: string|null }} [args]
 */
export function buildForwardingConfirmationMessage({ provider, code } = {}) {
  const who = provider === "outlook" ? "Outlook" : "Gmail";
  const lines = [`${who} möchte die Weiterleitungsadresse bestätigen.`];
  if (code) lines.push(`Code: ${code}`);
  lines.push(
    "Gib den Code in den Weiterleitungseinstellungen ein, oder öffne die Bestätigung. Danach gilt der Filter.",
  );
  return lines.join("\n");
}

/**
 * @param {{ hasHousehold?: boolean }} [opts]
 */
export function buildKitaShareChatMessage({ hasHousehold = false } = {}) {
  if (hasHousehold) {
    return "Kennen andere Eltern das auch — zum Beispiel in der Eltern-Gruppe?";
  }
  return "Soll dein Partner das auch sehen?";
}

export { buildShippedIdeaMessage } from "./shippedIdeaMessage";
